/**
 * The design-D flow's decisions, as pure functions (no DOM, no app state): which step of the bar a flow step is, which
 * steps can be opened, what is missing on a step, an honest time left for reading, the start screen's privacy title.
 * The screens (src/ui/flowD/screens.ts) draw what these say; src/ui/app.ts gives them the app's state.
 */

/** The bar's four steps (Matt, 2026-10-22: About you · Payslips · Questions · Results). */
export type BarStep = 'about' | 'documents' | 'questions' | 'results';
export const BAR_STEPS: readonly BarStep[] = ['about', 'documents', 'questions', 'results'];
export type FlowStep = 'goal' | 'who' | 'upload' | 'questions' | 'confirm' | 'results';

/** The bar step a flow step belongs to ("Check your answers" is part of Questions); the start screen has none. */
export function barStepOf(step: FlowStep): BarStep | undefined {
  return ({ goal: undefined, who: 'about', upload: 'documents', questions: 'questions', confirm: 'questions', results: 'results' } as const)[step];
}

/** The flow step a bar step opens. */
export function flowStepOf(step: BarStep): FlowStep {
  return ({ about: 'who', documents: 'upload', questions: 'questions', results: 'results' } as const)[step];
}

/** What is complete so far: "About you" answered (and not a stop), documents read and nothing waiting on the person, the answers confirmed. */
export interface Progress {
  about: boolean;
  documents: boolean;
  confirmed: boolean;
}

export type BarState = 'done' | 'current' | 'open' | 'locked';

/**
 * Each bar step's state: the current one; a step before it is `done` (a link back); a later step is `open` (a link
 * forward) only when every step before it is complete, else `locked` (Matt: "later steps locked until their inputs are
 * complete").
 */
export function barStates(current: BarStep, p: Progress): Record<BarStep, BarState> {
  const at = BAR_STEPS.indexOf(current);
  const reachable: Record<BarStep, boolean> = { about: true, documents: p.about, questions: p.about && p.documents, results: p.about && p.documents && p.confirmed };
  return Object.fromEntries(BAR_STEPS.map((s, i) => [s, i === at ? 'current' : i < at ? 'done' : reachable[s] ? 'open' : 'locked'])) as Record<BarStep, BarState>;
}

/** One thing a step still needs before Continue: its element's id (scrolled to and marked), the line's words, the message there. */
export interface Needed {
  id: string;
  /** i18n key: a few words for the line above Continue ("which describes you"). */
  name: string;
  /** Values for `name` (e.g. the month a question is about). */
  params?: Record<string, string | number>;
  /** i18n key: the message shown at the item ("Choose one of these to continue"); '' when the item says it itself. */
  message: string;
}

/** "Who are you?": the group, the sector on the payslip goals, and the checklist (unticking everything = not answered, never "None of these"). */
export function whoNeeded(a: { group?: string | undefined; statusesUnanswered?: boolean | undefined; sectorMissing?: boolean | undefined }): Needed[] {
  return [
    ...(a.group ? [] : [{ id: 'fd-who-groups', name: 'whoD.needed.group', message: 'whoD.needed.groupMessage' }]),
    // The payslip goals: the sector decides whether payslips can be checked at all (public: a stop), so it is asked here, required.
    ...(a.sectorMissing ? [{ id: 'fd-who-sector', name: 'whoD.needed.sector', message: 'whoD.needed.sectorMessage' }] : []),
    ...(a.statusesUnanswered ? [{ id: 'fd-who-statuses', name: 'whoD.needed.statuses', message: 'whoD.needed.statusesMessage' }] : []),
  ];
}

/**
 * Time left for the files still to read, only when it can be honest: from this session's measured reading times
 * (milliseconds per file, the engine start not included), the files waiting and how long the current one has run.
 * Undefined (say nothing) before any file has been timed, or when nothing is left.
 */
export function timeLeftMs(measured: readonly number[], waiting: number, currentElapsed: number | undefined): number | undefined {
  if (!measured.length) return undefined;
  const per = measured.reduce((a, b) => a + b, 0) / measured.length;
  const current = currentElapsed === undefined ? 0 : Math.max(per - currentElapsed, per * 0.1);
  const left = current + waiting * per;
  return left > 0 ? left : undefined;
}

/** Whole minutes to say ("About 2 minutes left"), rounded up; 0 means "less than a minute". */
export function minutesToSay(ms: number): number {
  return ms < 60_000 ? 0 : Math.ceil(ms / 60_000);
}

/**
 * The start screen's privacy card shows the approved line (app.privacyLine.*, Matt 13:36) as it is, its first two
 * sentences ("No sign-up. No names.") as the card's title. Split at sentence ends only where the text has at least three
 * sentences; otherwise the whole line is the body (a language whose punctuation we can't split is shown unchanged).
 */
export function splitPrivacyTitle(text: string): { title: string; body: string } {
  const ends = [...text.matchAll(/[.!?。።]["”’)]?(\s+|$)/g)];
  if (ends.length < 3) return { title: '', body: text };
  const second = ends[1];
  const cut = (second?.index ?? 0) + (second?.[0].length ?? 0);
  return { title: text.slice(0, cut).trim(), body: text.slice(cut).trim() };
}
